import { useEffect, useState } from "react";

import { dbFirestore } from "../../firestore";

import type { IssuedTicket } from "../../firestore/types";

/**
 * 指定された複数eventIdのissuedTicketsをリアルタイム同期する
 * enablePersistence()が有効な場合、オフライン時はIndexedDBキャッシュから返す
 */
export const useIssuedTicketMap = (
  eventIds: string[] | undefined
): {
  issuedTicketMap: Record<string, IssuedTicket>;
  error: Error | null;
} => {
  const [issuedTicketsByEvent, setIssuedTicketsByEvent] = useState<
    Record<string, Record<string, IssuedTicket>>
  >({});
  const [error, setError] = useState<Error | null>(null);

  // 依存配列でeventIdsを安定させるためにsort済み文字列を利用する
  const eventIdsKey = eventIds ? [...eventIds].sort().join(",") : "";

  useEffect(() => {
    if (!eventIds || eventIds.length === 0) {
      setIssuedTicketsByEvent({});
      return;
    }

    setError(null);
    setIssuedTicketsByEvent({});

    const unsubscribes = eventIds.map((eventId) =>
      dbFirestore.issuedTickets(eventId).onSnapshot(
        (snapshot) => {
          const tickets: Record<string, IssuedTicket> = {};
          snapshot.forEach((doc) => {
            const data = doc.data();
            if (data !== null) {
              tickets[doc.id] = data;
            }
          });
          setIssuedTicketsByEvent((prev) => ({ ...prev, [eventId]: tickets }));
        },
        (err) => {
          console.error(
            `Failed to listen issuedTickets (eventId=${eventId}):`,
            err
          );
          setError(err);
        }
      )
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
    // 依存配列にはeventIdsを入れる必要があるが、
    // eventIdsを直接依存に入れると、参照のたびに新しい配列になるため、安定したキーを利用する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdsKey]);

  const issuedTicketMap: Record<string, IssuedTicket> = Object.values(
    issuedTicketsByEvent
  ).reduce((acc, tickets) => ({ ...acc, ...tickets }), {});

  return { issuedTicketMap, error };
};
